import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, Bars3BottomLeftIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { ThemeSegmentedControl } from '../ThemeToggle';
import { isNavItemActive } from './platformNav';
import { useNavStyle } from './navStyle';

/*
 * The account menu both navigations share (avatar in the top bar, name row at the foot of the sidebar): who is
 * signed in, their account pages, optional quick links, Appearance, the Navigation switch and Sign out.
 */

export function initialsOf(user) {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || user?.email || '';
  const parts = String(name).split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
}

export function displayNameOf(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || user?.email || 'Account';
}

export function NavStyleControl() {
  const [style, setStyle] = useNavStyle();
  const options = [
    { id: 'sidebar', label: 'Sidebar' },
    { id: 'top', label: 'Top bar' },
  ];
  return (
    <div role="radiogroup" aria-label="Navigation" className="flex gap-0.5 rounded-lg bg-gray-800 p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={style === o.id}
          onClick={() => setStyle(o.id)}
          className={`flex-1 rounded-md px-2 py-1 text-xs ${style === o.id ? 'bg-gray-950 text-white shadow' : 'text-gray-400 hover:text-white'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Row({ item, current, onPick }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      role="menuitem"
      onClick={onPick}
      aria-current={current ? 'page' : undefined}
      className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-sm text-gray-200 hover:bg-gray-800 ${current ? 'bg-gray-800' : ''}`}
    >
      {Icon ? <Icon className="h-4 w-4 text-gray-500" /> : null}
      <span className="min-w-0 truncate">{item.name}</span>
    </Link>
  );
}

/**
 * variant 'avatar': the round initials button, menu drops below on the right (top bar).
 * variant 'row': a full-width name row, menu opens above it (sidebar foot).
 */
export default function AccountMenu({ user, items = [], quick = [], money = null, onLogout, variant = 'avatar', highlighted = false }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);
  const close = () => setOpen(false);
  const name = displayNameOf(user);

  const trigger = variant === 'row' ? (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label="Account"
      className="flex w-full min-w-0 items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-gray-800"
    >
      <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-primary-900/40 text-xs font-semibold text-primary-300">{initialsOf(user)}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-white">{name}</span>
        <span className="block truncate text-xs text-gray-500">{user?.email}</span>
      </span>
      <ChevronUpDownIcon className="h-4 w-4 flex-none text-gray-500" />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label="Account"
      className={`ml-0.5 grid h-8 w-8 place-items-center rounded-full bg-primary-900/40 text-xs font-semibold text-primary-300 hover:ring-2 hover:ring-primary-800 ${highlighted ? 'ring-2 ring-primary-700' : ''}`}
    >
      {initialsOf(user)}
    </button>
  );

  return (
    <div ref={ref} className={`relative ${variant === 'row' ? 'min-w-0 flex-1' : ''}`}>
      {trigger}
      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className={`absolute z-50 w-72 rounded-xl border border-gray-800 bg-gray-900 p-2 shadow-2xl ${variant === 'row' ? 'bottom-full left-0 mb-1.5' : 'right-0 top-full mt-1.5'}`}
        >
          <div className="px-2 pb-2 pt-1">
            <div className="truncate text-sm font-semibold text-white">{name}</div>
            <div className="truncate text-xs text-gray-500">{user?.email}</div>
          </div>
          {money ? (
            <Link to={money.href} onClick={close} className="mx-1 mb-1 flex items-center justify-between rounded-lg border border-gray-800 px-2.5 py-1.5 text-xs text-gray-400 hover:border-gray-700">
              {money.label}
              <b className="font-semibold tabular-nums text-white">{money.value}</b>
            </Link>
          ) : null}
          <div className="border-t border-gray-800 pt-1">
            {items.map((item) => (
              <Row key={item.href} item={item} current={isNavItemActive(pathname, item.href)} onPick={close} />
            ))}
          </div>
          {quick.length ? (
            <div className="mt-1 border-t border-gray-800 pt-1">
              {quick.map((item) => (
                <Row key={item.href} item={item} current={isNavItemActive(pathname, item.href)} onPick={close} />
              ))}
            </div>
          ) : null}
          <div className="mt-1 space-y-2 border-t border-gray-800 px-2 pb-1 pt-2">
            <div>
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-500">Appearance</div>
              <ThemeSegmentedControl />
            </div>
            <div>
              <div className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-gray-500">
                <Bars3BottomLeftIcon className="h-3.5 w-3.5" />
                Navigation
              </div>
              <NavStyleControl />
            </div>
          </div>
          <div className="mt-1 border-t border-gray-800 pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); onLogout?.(); }}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-500" />
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
